import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect, useDispatch } from 'react-redux';
import { queryCache } from 'react-query';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import { logoutUser } from '../../modules/global/actions';
import { fetchCustomerData } from '../../modules/customer/actions';
import { selectCustomerData, selectIsFetching } from '../../modules/customer/selectors';
import { getAccountType, allOfferingTypes, OfferingType } from '../../utils/offeringType';
import ContentWrapper from './ContentWrapper';
import SideNavigation from './SideNavigation';
import PageContent from './PageContent';
import Menu from './Menu/Menu';
import LogoutButton from './LogoutButton';
import { getQueryKeyVariables } from '../../utils/apiHooks';
import { getRouteByPathName } from '../../utils/routes';
import { getEnumById, getAccountId, getAccessToken, isFlutterUser, redirectToFlutter } from '../../utils/helpers';
import { gaSendUserLogin } from '../../utils/analytics';
import { loadMunMoiFlutter } from '../../utils/dynamicLoad';

interface Props {
	customerData: any;
	isFetching: any;
	children: React.ReactNode;
}

export const AccountPageImpl: React.FC<Props> = (props: Props) => {
	const [menuExpanded, setMenuExpanded] = useState(false);
	const dispatch = useDispatch();
	const location = useLocation();
	const history = useHistory();
	const { customerData, isFetching } = props;

	useEffect(() => {
		dispatch(fetchCustomerData());
	}, [dispatch]);

	useEffect(() => {
		if (customerData) {
			const accessToken = getAccessToken();
			const offeringType = getEnumById(customerData.toJS().emoSubscriptionType, allOfferingTypes);
			if (isFlutterUser(accessToken, offeringType)) {
				loadMunMoiFlutter();
				redirectToFlutter();
			} else {
				const accountId = getAccountId();
				gaSendUserLogin(accountId, offeringType);
			}
		}
	}, [customerData]);
	const children = React.Children.toArray(props.children);
	const getChildrenWithAccountType = (offeringType: OfferingType) => {
		if (customerData) {
			return React.Children.map(
				children,
				(child: any) => React.cloneElement(child, { accountType: getAccountType(offeringType), offeringType }) // TODO: accountType can be determined from emoSubscriptionType so it can be removed
			);
		} else {
			return null;
		}
	};

	const handleLogout = (e: any) => {
		e.preventDefault();
		const sourcePage = location.pathname;
		dispatch(
			logoutUser(() => {
				queryCache.removeQueries((query) => {
					const accountId = getQueryKeyVariables(query)?.accountId;
					return accountId !== undefined;
				});
				history.replace(`/login?redirect=${sourcePage}`);
			})
		);
	};

	if (customerData) {
		const offeringType = getEnumById(customerData.toJS().emoSubscriptionType, allOfferingTypes);
		if (getRouteByPathName(location.pathname)!.authorized(customerData.toJS()) && !isFlutterUser(getAccessToken(), offeringType)) {
			return (
				<React.Fragment>
					<Helmet title="Account" meta={[{ name: 'description', content: 'Account' }]} />
					<ContentWrapper>
						<SideNavigation expanded={menuExpanded}>
							<Menu path={location.pathname} customerData={customerData.toJS()} />
							<LogoutButton onClick={handleLogout} />
						</SideNavigation>
						<PageContent onClick={() => setMenuExpanded(!menuExpanded)}>{getChildrenWithAccountType(offeringType)}</PageContent>
					</ContentWrapper>
				</React.Fragment>
			);
		} else {
			return <Redirect to="/account/dashboard" />;
		}
	} else if (isFetching) {
		return null;
	} else {
		return (
			<React.Fragment>
				<Helmet title="Account" meta={[{ name: 'description', content: 'Account' }]} />
				<ContentWrapper>
					<SideNavigation expanded={menuExpanded}>
						<LogoutButton onClick={handleLogout} />
					</SideNavigation>
				</ContentWrapper>
			</React.Fragment>
		);
	}
};

const mapStateToProps = (state: any) => ({
	customerData: selectCustomerData(state),
	isFetching: selectIsFetching(state)
});

export const AccountPage = connect(mapStateToProps)(AccountPageImpl);
